import React, { useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import FormFieldLabel from './FormFieldLabel';
import { BRAND_ORANGE, PLACEHOLDER_COLOR } from './manageEventFieldStyles';

const MenuFileUpload = ({
  label,
  file,
  onFileChange,
  accept,
  disabled = false,
  validateFile,
  formatHint,
  invalidFileMessage = 'Please select a PDF or image file.',
  buttonOnly = false,
}) => {
  const inputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) {
      setFileError('');
      onFileChange(null);
      return;
    }

    if (validateFile && !validateFile(selectedFile)) {
      setFileError(invalidFileMessage);
      return;
    }

    setFileError('');
    onFileChange(selectedFile);
  };

  return (
    <Box sx={{ opacity: disabled ? 0.55 : 1 }}>
      <Box sx={{ mb: 1 }}>
        <FormFieldLabel
          sx={{ mb: formatHint ? 0.25 : 0, color: disabled ? 'text.disabled' : undefined }}
        >
          {label}
        </FormFieldLabel>
        {formatHint ? (
          <Typography
            variant="caption"
            sx={{
              color: PLACEHOLDER_COLOR,
              fontWeight: 400,
              fontSize: '0.6875rem',
              lineHeight: 1.2,
              display: 'block',
            }}
          >
            {formatHint}
          </Typography>
        ) : null}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="outlined"
          disabled={disabled}
          startIcon={<AttachFileOutlinedIcon />}
          onClick={() => inputRef.current?.click()}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 1.25,
            color: BRAND_ORANGE,
            borderColor: BRAND_ORANGE,
            '&:hover': {
              borderColor: 'primary.dark',
              bgcolor: 'rgba(224, 104, 10, 0.04)',
            },
          }}
        >
          {buttonOnly ? 'Upload file' : 'Choose file'}
        </Button>
        {!buttonOnly ? (
          <>
            <Typography variant="body2" sx={{ color: file ? 'text.primary' : PLACEHOLDER_COLOR }}>
              {file?.name || 'No file selected'}
            </Typography>
            {file && !disabled ? (
              <Button
                size="small"
                onClick={() => handleFileSelect(null)}
                sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 'auto', p: 0.5 }}
              >
                Remove
              </Button>
            ) : null}
          </>
        ) : null}
        <input
          ref={inputRef}
          type="file"
          hidden
          disabled={disabled}
          accept={accept}
          onChange={(e) => {
            handleFileSelect(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
      </Box>
      {fileError ? (
        <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.75, display: 'block' }}>
          {fileError}
        </Typography>
      ) : null}
    </Box>
  );
};

export default MenuFileUpload;
